import { useState } from 'react'
import { Card } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog'
import { Loader2, Plus, Search, Layers, Trash2 } from 'lucide-react'
import { useAdminCarBrands, useAdminCarModels, useCreateModel, useDeleteModel } from '../../../hooks/use-admin-cars'
import { DeleteConfirmDialog } from '../components/delete-confirm-dialog'

export function ModelsPage() {
  const { data: brands } = useAdminCarBrands()
  const [selectedBrandId, setSelectedBrandId] = useState<number | undefined>(undefined)

  const activeBrandId = selectedBrandId ?? (brands?.[0]?.id || 0)
  const { data: models, isLoading } = useAdminCarModels(activeBrandId)
  const createModel = useCreateModel()
  const deleteModel = useDeleteModel()

  const [search, setSearch] = useState('')
  const [open, setOpen] = useState(false)
  const [deleteTarget, setDeleteTarget] = useState<any>(null)
  const [name, setName] = useState('')
  const [bodyType, setBodyType] = useState('SUV')

  const filteredModels = (models || []).filter((m: any) =>
    m.name.toLowerCase().includes(search.toLowerCase())
  )

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!name.trim() || !activeBrandId) return
    await createModel.mutateAsync({
      brandId: activeBrandId,
      name,
      bodyType,
    })
    setName('')
    setOpen(false)
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl font-bold tracking-tight">Car Models Catalog</h1>
          <p className="text-sm text-muted-foreground">Manage canonical models for each car brand (Brand → Model strict hierarchy).</p>
        </div>

        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger>
            <Button disabled={!activeBrandId} className="gap-2">
              <Plus className="size-4" /> Add Model
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add Model under {brands?.find((b: any) => b.id === activeBrandId)?.name}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreate} className="space-y-4 pt-4">
              <div className="space-y-1">
                <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Model Name *</label>
                <Input placeholder="e.g. Fortuner / 3 Series" value={name} onChange={(e) => setName(e.target.value)} required />
              </div>
              <div className="space-y-1">
                <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Body Type</label>
                <select value={bodyType} onChange={(e) => setBodyType(e.target.value)} className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm font-medium">
                  <option value="SUV">SUV</option>
                  <option value="Sedan">Sedan</option>
                  <option value="Hatchback">Hatchback</option>
                  <option value="Coupe">Coupe</option>
                  <option value="Convertible">Convertible</option>
                  <option value="MUV">MUV / MPV</option>
                </select>
              </div>
              <div className="pt-4 flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                <Button type="submit" disabled={createModel.isPending}>
                  {createModel.isPending ? <Loader2 className="size-4 animate-spin" /> : 'Save Model'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Brand Selector Bar */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
        <div className="w-full sm:w-64">
          <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground block mb-1">Select Brand</label>
          <select value={String(activeBrandId)} onChange={(e) => setSelectedBrandId(Number(e.target.value))} className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm font-medium">
            {(brands || []).map((b: any) => (
              <option key={b.id} value={String(b.id)}>{b.name}</option>
            ))}
          </select>
        </div>

        <div className="relative flex-1 w-full max-w-md pt-5">
          <Search className="absolute left-3 top-7 size-4 text-muted-foreground" />
          <Input placeholder="Search models..." className="pl-9" value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
      </div>

      {/* Models Table */}
      {isLoading ? (
        <div className="p-12 text-center flex flex-col items-center justify-center space-y-3">
          <Loader2 className="size-8 animate-spin text-primary" />
          <p className="text-sm text-muted-foreground">Loading vehicle models...</p>
        </div>
      ) : (
        <Card className="overflow-hidden border border-border">
          <table className="w-full text-left text-sm">
            <thead className="bg-accent/40 text-muted-foreground border-b border-border uppercase text-[10px] tracking-wider">
              <tr>
                <th className="p-4">Model Name</th>
                <th className="p-4">Slug</th>
                <th className="p-4">Body Type</th>
                <th className="p-4">Generations</th>
                <th className="p-4">Listings</th>
                <th className="p-4">Status</th>
                <th className="p-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {filteredModels.map((m: any) => {
                const isIndependent = (m._count?.generations ?? 0) === 0 && (m._count?.listings ?? 0) === 0
                return (
                  <tr key={m.id} className="hover:bg-accent/20 transition-colors">
                    <td className="p-4 font-bold flex items-center gap-2">
                      <Layers className="size-4 text-primary" />
                      <span>{m.name}</span>
                    </td>
                    <td className="p-4 font-mono text-xs text-muted-foreground">{m.slug}</td>
                    <td className="p-4 font-semibold">{m.bodyType || 'SUV'}</td>
                    <td className="p-4 font-semibold">{m._count?.generations ?? 0}</td>
                    <td className="p-4 font-semibold">{m._count?.listings ?? 0}</td>
                    <td className="p-4">
                      <Badge className={m.isActive ? 'bg-emerald-500/20 text-emerald-500' : 'bg-muted text-muted-foreground'}>
                        {m.isActive ? 'Active' : 'Inactive'}
                      </Badge>
                    </td>
                    <td className="p-4 text-right">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setDeleteTarget(m)}
                        className={`size-8 p-0 ${
                          isIndependent
                            ? 'text-rose-500 hover:text-rose-600 hover:bg-rose-500/10'
                            : 'text-muted-foreground/50 hover:text-amber-600 hover:bg-amber-500/10'
                        }`}
                        title={
                          isIndependent
                            ? `Delete independent model "${m.name}"`
                            : `Cannot delete: Has ${m._count?.generations || 0} generations and ${m._count?.listings || 0} listings`
                        }
                      >
                        <Trash2 className="size-4" />
                      </Button>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </Card>
      )}

      {deleteTarget && (
        <DeleteConfirmDialog
          isOpen={!!deleteTarget}
          onClose={() => setDeleteTarget(null)}
          onConfirm={async () => {
            await deleteModel.mutateAsync({ id: deleteTarget.id, brandId: activeBrandId })
          }}
          title={`Delete Model: ${deleteTarget.name}`}
          entityName={deleteTarget.name}
          entityType="Model"
          isIndependent={(deleteTarget._count?.generations ?? 0) === 0 && (deleteTarget._count?.listings ?? 0) === 0}
          blockingReason={`Cannot delete model "${deleteTarget.name}" because it has ${deleteTarget._count?.generations || 0} generation(s) and ${deleteTarget._count?.listings || 0} listing(s) attached. Only independent models can be deleted.`}
          dependencyDetails={[
            { label: 'Generations', count: deleteTarget._count?.generations ?? 0 },
            { label: 'Listings', count: deleteTarget._count?.listings ?? 0 },
          ]}
        />
      )}
    </div>
  )
}
